// PhotoSwipe Enhancements
export class PhotoSwipeEnhanced {
    constructor() {
        this.debug = true; // Enable debug logging
        this.loadingImages = new Set(); // Track loading images
        this.initializePhotoSwipeEnhancements();
    }

    log(...args) {
        if (this.debug) {
            console.log('[PhotoSwipe Enhanced]', ...args);
        }
    }

    initializePhotoSwipeEnhancements() {
        // Wait for ProductGallery to be defined
        customElements.whenDefined('product-gallery').then(() => {
            this.log('ProductGallery defined, enhancing PhotoSwipe...');

            // Enhance PhotoSwipe configuration
            this.enhancePhotoSwipe();

            // Add loading indicator styles
            this.addLoadingIndicatorStyles();
        });
    }

    enhancePhotoSwipe() {
        // Store reference to the enhancer instance
        const enhancerInstance = this;

        // Override the lightBox getter to inject our enhanced configuration
        const ProductGallery = window.customElements.get('product-gallery');
        const originalDescriptor = Object.getOwnPropertyDescriptor(ProductGallery.prototype, 'lightBox');

        Object.defineProperty(ProductGallery.prototype, 'lightBox', {
            get: function () {
                // If instance already exists, return it
                if (this.__photoSwipeInstance) {
                    return this.__photoSwipeInstance;
                }

                // Call original getter to create the instance
                const instance = originalDescriptor.get.call(this);

                // Enhance the instance configuration before it's used
                if (instance && !instance.__enhanced) {
                    instance.__enhanced = true;

                    // Override options to increase zoom levels
                    const originalInit = instance.init.bind(instance);
                    instance.init = function () {
                        // Update options before initialization
                        this.options.maxZoomLevel = 8;
                        this.options.initialZoomLevel = 'fit';
                        this.options.secondaryZoomLevel = 2.5;
                        this.options.maxSpreadZoom = 8;

                        // Enable high-res image loading on zoom
                        this.options.allowPanToNext = false; // Disable pan to next when zoomed
                        this.options.getDoubleTapZoom = (isMouseClick, item) => {
                            // Custom zoom levels: fit -> 2.5x -> 5x -> 8x
                            if (item.initialZoomLevel === item.currZoomLevel) {
                                return 2.5;
                            } else if (item.currZoomLevel < 3) {
                                return 5;
                            } else if (item.currZoomLevel < 6) {
                                return 8;
                            } else {
                                return item.initialZoomLevel;
                            }
                        };

                        // Call original init
                        return originalInit();
                    };

                    // Add event listeners for loading states
                    instance.on('contentLoadImage', (e) => {
                        const { content, isLazy } = e;
                        enhancerInstance.log('contentLoadImage', { content, isLazy });

                        if (content.data.src && !enhancerInstance.loadingImages.has(content.data.src)) {
                            enhancerInstance.loadingImages.add(content.data.src);
                            enhancerInstance.log('Started loading:', content.data.src);

                            // Add loading indicator to the container, not the image
                            const container = e.slide?.container;
                            if (container && !container.querySelector('.pswp-loading-indicator')) {
                                const loadingIndicator = document.createElement('div');
                                loadingIndicator.className = 'pswp-loading-indicator';
                                loadingIndicator.innerHTML = `
                  <div class="pswp-loading-spinner"></div>
                  <div class="pswp-loading-text">Loading HD...</div>
                `;
                                container.appendChild(loadingIndicator);
                            }
                        }
                    });

                    instance.on('contentLoad', (e) => {
                        const { content, isLazy } = e;
                        enhancerInstance.log('contentLoad', { content, isLazy });

                        if (content.data.src) {
                            enhancerInstance.loadingImages.delete(content.data.src);
                            enhancerInstance.log('Finished loading:', content.data.src);

                            // Remove loading indicator
                            const container = e.slide?.container;
                            const indicator = container?.querySelector('.pswp-loading-indicator');
                            if (indicator) {
                                indicator.remove();
                            }
                        }
                    });

                    instance.on('contentLoadError', (e) => {
                        const { content, isLazy } = e;
                        enhancerInstance.log('contentLoadError', { content, isLazy });

                        if (content.data.src) {
                            enhancerInstance.loadingImages.delete(content.data.src);

                            // Remove loading indicator on error
                            const container = e.slide?.container;
                            const indicator = container?.querySelector('.pswp-loading-indicator');
                            if (indicator) {
                                indicator.remove();
                            }
                        }
                    });

                    // Enable deep zoom for high resolution images
                    instance.on('firstZoomPan', (e) => {
                        enhancerInstance.log('firstZoomPan', e);
                    });

                    instance.on('zoomPanUpdate', ({ slide }) => {
                        if (slide && slide.currZoomLevel > 2) {
                            // Could implement tiled loading here if needed
                            enhancerInstance.log('High zoom detected:', slide.currZoomLevel);
                        }
                    });

                    instance.on('beforeZoomTo', (e) => {
                        // Enable smooth multi-level zoom
                        if (e.destZoomLevel > 1) {
                            instance.currSlide?.zoomTo(e.destZoomLevel, e.centerPoint, 333, false);
                        }
                    });
                }

                // Store enhanced instance
                this.__photoSwipeInstance = instance;
                return instance;
            },
            configurable: true
        });

        // Also update existing gallery zoom attributes
        document.querySelectorAll('product-gallery').forEach((gallery) => {
            const currentZoom = parseInt(gallery.getAttribute('allow-zoom')) || 3;
            gallery.setAttribute('allow-zoom', '8');
        });
    }

    addLoadingIndicatorStyles() {
        // Check if styles already exist
        if (document.getElementById('photoswipe-enhanced-styles')) return;

        const style = document.createElement('style');
        style.id = 'photoswipe-enhanced-styles';
        style.textContent = `
      /* Enhanced PhotoSwipe Loading Styles */
      .photoswipe-enhanced .pswp__img {
        transition: opacity 0.3s ease;
      }
      
      /* Loading state for images - no opacity change */
      .pswp-img-loading {
        /* Image stays fully visible while loading */
      }
      
      /* Loading indicator container - positioned in top-right corner */
      .pswp-loading-indicator {
        position: absolute;
        top: 20px;
        right: 20px;
        z-index: 10;
        pointer-events: none;
        background: rgba(0, 0, 0, 0.6);
        padding: 10px 15px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        gap: 10px;
      }
      
      /* Loading spinner - smaller and inline */
      .pswp-loading-spinner {
        width: 20px;
        height: 20px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: #fff;
        border-radius: 50%;
        animation: pswp-spinner 0.8s linear infinite;
      }
      
      /* Loading text - inline with spinner */
      .pswp-loading-text {
        color: white;
        font-size: 12px;
        font-weight: 500;
        font-family: var(--font-heading-family);
        white-space: nowrap;
      }
      
      @keyframes pswp-spinner {
        to { transform: rotate(360deg); }
      }
      
      /* Enhanced zoom cursor for PhotoSwipe */
      .pswp--zoomed-in .pswp__img {
        cursor: zoom-out !important;
      }
      
      .pswp:not(.pswp--zoomed-in) .pswp__img {
        cursor: zoom-in !important;
      }
      
      /* Better image rendering during zoom */
      .pswp__img {
        image-rendering: -webkit-optimize-contrast;
        image-rendering: crisp-edges;
      }
      
      /* Enable pinch and scroll zoom */
      .pswp__zoom-wrap {
        touch-action: none !important;
      }
      
      /* Smooth zoom transitions */
      .pswp--animated-in .pswp__zoom-wrap,
      .pswp--zoom-allowed .pswp__zoom-wrap {
        transition: transform 333ms cubic-bezier(0.4, 0, 0.22, 1);
      }
      
      /* Double-tap zoom indicator */
      .pswp--zoom-allowed .pswp__img {
        cursor: zoom-in !important;
      }
      
      .pswp--zoomed-in .pswp__img {
        cursor: zoom-out !important;
      }
    `;

        document.head.appendChild(style);
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new PhotoSwipeEnhanced());
} else {
    new PhotoSwipeEnhanced();
}
